import { Linkable } from '@/components/blocks/Linkable';
import { TableCellProductImage } from '@/components/blocks/ProductImage';
import { CatalogEntryListComparePrice } from '@/components/content/CatalogEntryList/parts/Compare/Price';
import { catalogEntryListCompareCollectorActionsButtonClearSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorActionsButtonClear';
import { catalogEntryListCompareCollectorActionsButtonCompareSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorActionsButtonCompare';
import { catalogEntryListCompareCollectorBarSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorBar';
import { catalogEntryListCompareCollectorBarContentSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorBarContent';
import { CollectorBrandSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorBrand';
import { catalogEntryListCompareCollectorCloseIconSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorCloseIcon';
import { catalogEntryListCompareCollectorDescriptionSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorDescription';
import { catalogEntryListCompareCollectorDiagnosticSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorDiagnostic';
import { catalogEntryListCompareCollectorDiagnosticLinkSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorDiagnosticLink';
import { catalogEntryListCompareCollectorDrawerContainerSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorDrawerContainer';
import { catalogEntryListCompareCollectorPriceSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorPrice';
import { catalogEntryListCompareCollectorPriceDescSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorPriceDesc';
import { CollectorProductCardSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorProductCard';
import { catalogEntryListCompareCollectorRelativeContainerSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorRelativeContainer';
import { catalogEntryListCompareCollectorStickyContainerSX } from '@/components/content/CatalogEntryList/styles/Compare/collectorStickyContainer';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useCompareCollectorV2 } from '@/data/Content/CompareCollectorV2';
import { useLocalization } from '@/data/Localization';
import { ContentContext } from '@/data/context/content';
import { ProductType } from '@/data/types/Product';
import { getProductThumbnail } from '@/utils/productImage';
import {
	Box,
	Button,
	Container,
	Grid,
	IconButton,
	Stack,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useContext } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

export const CatalogEntryListCompareCollector: FC = () => {
	const { compareState, removeAll, compareEnabled, anchorRef, open, openCompare, onRemove } =
		useContext(ContentContext) as ReturnType<typeof useCompareCollectorV2>;
	const localization = useLocalization('compare');
	const theme = useTheme();
	const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

	return compareEnabled ? (
		<Stack ref={anchorRef} sx={catalogEntryListCompareCollectorStickyContainerSX}>
			<Stack sx={catalogEntryListCompareCollectorRelativeContainerSX}>
				<Stack
					sx={catalogEntryListCompareCollectorDrawerContainerSX(undefined, isTablet ? '58px' : 0)}
				>
					<Container sx={catalogEntryListCompareCollectorBarSX}>
						<Grid container py={2}>
							<Grid item container sm={9} md={10} spacing={1}>
								<Grid item>
									{compareState.len > 1 ? (
										<Button
											id="compare-selected-products"
											data-testid="compare-selected-products"
											variant="contained"
											sx={catalogEntryListCompareCollectorActionsButtonCompareSX}
											onClick={openCompare}
										>
											{localization.CompareBtn.t()}
										</Button>
									) : (
										<Typography variant="body2" sx={catalogEntryListCompareCollectorDiagnosticSX}>
											{localization.SelectMore.t()}
										</Typography>
									)}
								</Grid>
								<Grid item>
									<Button
										id="button-clear-selection"
										data-testid="button-clear-selection"
										variant="outlined"
										sx={catalogEntryListCompareCollectorActionsButtonClearSX}
										onClick={removeAll}
									>
										{localization.clearSel.t()}
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Container>
					{open ? (
						<Container sx={catalogEntryListCompareCollectorBarContentSX}>
							<Grid container>
								<Grid item container sm={12} marginBottom={2}>
									{compareState.storage.filter(Boolean).map((obj: ProductType, i: number) => (
										<Grid item key={i} xs={12 / compareState.max} sx={CollectorProductCardSX}>
											<IconButton
												size="large"
												id={`button-product-compare-${obj.partNumber?.toLowerCase()}-close`}
												data-testid={`button-product-compare-${obj.partNumber?.toLowerCase()}-close`}
												sx={catalogEntryListCompareCollectorCloseIconSX}
												color="primary"
												onClick={onRemove(obj)}
											>
												<DynamicIcon name={MATERIAL_SYMBOL_ICONS.cancel} />
											</IconButton>
											<TableCellProductImage src={getProductThumbnail(obj)} alt={obj.name} />
											<Stack sx={catalogEntryListCompareCollectorPriceDescSX}>
												<Typography sx={CollectorBrandSX}>{obj.manufacturer}</Typography>
												<Linkable
													href={obj.seo?.href}
													data-testid={obj.seo?.href}
													id={obj.seo?.href}
													sx={catalogEntryListCompareCollectorDiagnosticLinkSX}
												>
													<Typography sx={catalogEntryListCompareCollectorDescriptionSX}>
														{obj.name}
													</Typography>
												</Linkable>
												<Box sx={catalogEntryListCompareCollectorPriceSX}>
													<CatalogEntryListComparePrice product={obj} compact={isTablet} />
												</Box>
											</Stack>
										</Grid>
									))}
									{Array(compareState.max - compareState.len)
										.fill(null)
										.map((n, i) => (
											<Grid item key={i} xs={12 / compareState.max} sx={CollectorProductCardSX}>
												<Grid>
													<Typography
														variant="body2"
														sx={catalogEntryListCompareCollectorDiagnosticSX}
													>
														{localization.boxNofM.t({
															m: compareState.max,
															n: compareState.len + i + 1,
														})}
													</Typography>
												</Grid>
											</Grid>
										))}
								</Grid>
							</Grid>
						</Container>
					) : null}
				</Stack>
			</Stack>
		</Stack>
	) : null;
};
